/**
 * Pricing tiers from requirements/pricing-strategy.md
 */

export type Tier = "anonymous" | "free" | "pro" | "teams" | "enterprise";

export const DEFAULT_TIER: Tier = "anonymous";

export const TIERS: { value: Tier; label: string }[] = [
  { value: "anonymous", label: "Anonymous" },
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "teams", label: "Teams" },
  { value: "enterprise", label: "Enterprise" },
];

export function isAuthenticated(tier: Tier): boolean {
  return tier !== "anonymous";
}

export function isPaidTier(tier: Tier): boolean {
  return tier === "pro" || tier === "teams" || tier === "enterprise";
}

/**
 * Returns the earliest year accessible for a tier.
 * Returns null if unlimited access.
 */
export function getArchiveCutoffYear(tier: Tier): number | null {
  const { archiveYears } = TIER_LIMITS[tier];
  if (archiveYears === "unlimited") return null;
  return new Date().getFullYear() - archiveYears;
}

/**
 * Tier constraints from requirements/pricing-strategy.md
 *
 * Note: Holdings visibility and analytics gating were removed (2026-01-12).
 * See requirements/subscriptions/adr/004-tier-gating-removal.md
 */
export interface TierLimits {
  /** Edge + browser cache TTL. Longer TTL for lower tiers reduces server load. */
  cacheTtlMs: number;
  /** Can export holdings data */
  exportsEnabled: boolean;
  /** Has programmatic API access */
  apiAccess: boolean;
  /** Monthly /api request quota (null = unlimited) */
  monthlyQuota: number | null;
  /** Archive depth in years ("unlimited" for full access) */
  archiveYears: number | "unlimited";
  /** Query history retention in days (0 = no storage, -1 = unlimited) */
  historyRetentionDays: number;
}

export const TIER_LIMITS: Record<Tier, TierLimits> = {
  anonymous: {
    cacheTtlMs: 7 * 24 * 60 * 60 * 1000, // 7 days - low-value traffic, cache aggressively
    exportsEnabled: false,
    apiAccess: false,
    monthlyQuota: 500,
    archiveYears: 1,
    historyRetentionDays: 0, // No storage for anonymous users
  },
  free: {
    cacheTtlMs: 48 * 60 * 60 * 1000, // 48 hours - signing up gets account persistence, not fresher data
    exportsEnabled: false,
    apiAccess: false,
    monthlyQuota: 1000,
    archiveYears: 2,
    historyRetentionDays: 30,
  },
  pro: {
    cacheTtlMs: 5 * 60 * 1000, // 5 minutes - paying users get fresh data
    exportsEnabled: true,
    apiAccess: false,
    monthlyQuota: 10000,
    archiveYears: 10,
    historyRetentionDays: 365,
  },
  teams: {
    cacheTtlMs: 5 * 60 * 1000,
    exportsEnabled: true,
    apiAccess: true,
    monthlyQuota: 100000,
    archiveYears: "unlimited",
    historyRetentionDays: -1, // Unlimited
  },
  enterprise: {
    cacheTtlMs: 5 * 60 * 1000,
    exportsEnabled: true,
    apiAccess: true,
    monthlyQuota: null, // unlimited
    archiveYears: "unlimited",
    historyRetentionDays: -1, // Unlimited
  },
};

/**
 * Pricing data for ft-pricing-table component.
 * Reflects requirements/pricing-strategy.md
 */
export interface PricingTier {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  cta: {
    text: string;
    /** URL path. If method is "post", fetches this URL and redirects to response.url */
    action: string;
    /** "get" (default) = navigate to action URL. "post" = POST then redirect. */
    method?: "get" | "post";
  };
}

const BASE_PRICING_DATA: PricingTier[] = [
  {
    name: "Free",
    price: "€0",
    description:
      "Research institutional holdings and 13F reports. No commitment.",
    features: [
      "All holdings and analytics",
      "AI search across 9,000+ filers",
      "Data refreshed every 48 hours",
      "Filing alerts via email",
    ],
    cta: {
      text: "Subscribe Free",
      action: "/",
    },
  },
  {
    name: "Pro",
    price: "€12",
    period: "month",
    description: "Real-time data and AI queries for active research.",
    features: [
      "Real-time data (5-min refresh)",
      "10 AI queries/month",
      "Export to CSV",
      "10 years of archives",
    ],
    highlighted: true,
    cta: {
      text: "Try Pro Free for 7 Days",
      action: "https://auth.formthirteen.com",
    },
  },
  {
    name: "Teams",
    price: "€39",
    period: "month",
    description: "AI monitors portfolios. You focus on decisions.",
    features: [
      "50 AI queries/month",
      "Scheduled portfolio monitoring",
      "Everything in Pro",
      "Up to 3 team seats",
      "20+ years of archives",
    ],
    cta: {
      text: "Automate Your Research",
      action: "https://auth.formthirteen.com",
    },
  },
];

/**
 * Returns pricing data with CTA actions adjusted for the user's tier.
 * - anonymous: Pro CTA → sign-in link
 * - free: Pro CTA → POST /api/v1/checkout
 * - pro+: Pro CTA → POST /api/v1/billing/portal (manage subscription)
 */
export function getPricingData(tier: Tier): PricingTier[] {
  return BASE_PRICING_DATA.map((t) => {
    if (t.name !== "Pro") return t;

    if (isPaidTier(tier)) {
      return {
        ...t,
        cta: {
          text: "Manage Subscription",
          action: "/api/v1/billing/portal",
          method: "post" as const,
        },
      };
    }
    if (isAuthenticated(tier)) {
      return {
        ...t,
        cta: {
          text: "Upgrade to Pro",
          action: "/api/v1/checkout",
          method: "post" as const,
        },
      };
    }
    return t;
  });
}

/** @deprecated Use getPricingData(tier) instead */
export const PRICING_DATA = BASE_PRICING_DATA;
